<template>
  <div class="lookbook-article">
    <!-- HEADER / INTRO -->
    <header class="intro">
      <div class="intro__meta">
        <div class="intro__title">
          <span class="intro__bullet" aria-hidden="true">■</span>
          <span class="intro__title-text">{{ title }}</span>
        </div>
        <div class="intro__date">{{ date }}</div>
      </div>

      <!-- EXCERPT + CTA -->
      <div v-if="excerpt || (ctaLabel && ctaUrl)" class="intro__excerpt">
        <p v-if="excerpt">
          {{ excerpt }}
        </p>

        <p v-if="ctaLabel && ctaUrl" class="intro__excerpt-cta">
          <a :href="ctaUrl">
            {{ ctaLabel }}
          </a>
        </p>
      </div>

      <!-- Everything that appears before the first <img> in the article HTML -->
      <div v-if="leadingHtml" class="intro__leading rte" v-html="leadingHtml" />
    </header>

    <!-- IMAGE GRID -->
    <div class="grid">
      <div v-for="(image, index) in images" :key="index" class="grid-item" @click="openLightbox(index)">
        <img :src="image.src" :alt="image.alt || 'Lookbook image'" />
        <p class="counter">{{ (index + 1).toString().padStart(2, '0') }}</p>
      </div>
    </div>

    <!-- LIGHTBOX -->
    <transition name="lightbox-fade">
      <div class="lightbox" v-if="activeIndex !== null" @click.self="closeLightbox">
        <button class="close-btn" @click="activeIndex = null">CLOSE</button>

        <div class="lightbox-content">
          <!-- Active image fade -->
          <transition name="lightbox-image-fade" mode="out-in">
            <img
              ref="activeImgEl"
              :key="activeIndex"
              :src="images[activeIndex].src"
              class="active-img"
              @load="updateCaptionMaxWidth"
              @click.stop="onActiveImgClick"
            />
          </transition>

          <!-- Caption row fade -->
          <transition name="caption-fade" mode="out-in">
            <div
              class="caption-row"
              :key="activeIndex"
              :style="captionMaxWidth ? { width: captionMaxWidth + 'px' } : {}"
            >
              <span class="lightbox-counter">
                {{ (activeIndex + 1).toString().padStart(2, '0') }}
              </span>
              <span class="caption" v-if="images[activeIndex].caption">
                {{ images[activeIndex].caption }}
              </span>
            </div>
          </transition>
        </div>

        <div class="thumb-wrapper prev-thumb" v-if="activeIndex > 0" @click="showPrev">
          <div class="thumb">
            <img :src="images[activeIndex - 1].src" alt="Previous Image" />
          </div>
          <p class="thumb-counter">{{ activeIndex.toString().padStart(2, '0') }}</p>
        </div>

        <div class="thumb-wrapper next-thumb" v-if="activeIndex < images.length - 1" @click="showNext">
          <div class="thumb">
            <img :src="images[activeIndex + 1].src" alt="Next Image" />
          </div>
          <p class="thumb-counter">{{ (activeIndex + 2).toString().padStart(2, '0') }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

type ImageItem = { src: string; alt?: string; caption?: string }

const props = defineProps<{
  articleHtml: string
  title?: string
  date?: string
  excerpt?: string
  ctaLabel?: string
  ctaUrl?: string
}>()

const images = ref<ImageItem[]>([])
const leadingHtml = ref('')
const activeIndex = ref<number | null>(null)

// Measure the active image + store its width for the caption row
const activeImgEl = ref<HTMLImageElement | null>(null)
const captionMaxWidth = ref<number | null>(null)

function updateCaptionMaxWidth() {
  const img = activeImgEl.value
  if (!img) return

  // Natural dimensions of the image
  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight

  if (!naturalWidth || !naturalHeight) {
    // Fallback: use element width if natural sizes aren't ready
    const fallbackRect = img.getBoundingClientRect()
    if (fallbackRect.width > 0) {
      captionMaxWidth.value = fallbackRect.width
    }
    return
  }

  // Constraints from layout:
  // 1) max-width: 100% of the .lightbox-content
  // 2) max-height: 70svh of the viewport
  const lightboxContent = img.closest('.lightbox-content') as HTMLElement | null
  const contentRect = lightboxContent?.getBoundingClientRect()
  const maxWidth = contentRect?.width ?? naturalWidth
  const maxHeight = window.innerHeight * 0.7 // matches max-height: 70svh

  // Object-fit: contain → scale by the limiting dimension
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1)
  const renderedWidth = naturalWidth * scale

  if (renderedWidth > 0) {
    captionMaxWidth.value = renderedWidth
  }
}

/** Try multiple places for a caption associated with an <img> */
function getCaptionFor(img: HTMLImageElement): string {
  const fig = img.closest('figure')
  const figcap = fig?.querySelector('figcaption')
  if (figcap) {
    const t = figcap.textContent?.trim()
    if (t) return t
  }

  const textFromNode = (node: Node | null) => (node?.textContent || '').replace(/\s+/g, ' ').trim()

  let n: Node | null = img.nextSibling
  while (n) {
    if (n.nodeType === 1) {
      const el = n as HTMLElement
      if (el.matches('img') || el.querySelector('img')) break
      if (el.matches('p, small, em, .caption, span, div')) {
        const t = textFromNode(el)
        if (t) return t
      }
    } else if (n.nodeType === 3) {
      const t = textFromNode(n)
      if (t) return t
    }
    n = n.nextSibling
  }

  const container = img.closest('p, div, section, article') || img.parentElement
  let sib: Node | null = container?.nextSibling || null
  while (sib) {
    if (sib.nodeType === 1) {
      const el = sib as HTMLElement
      if (el.matches('img') || el.querySelector('img')) break
      if (el.matches('p, small, em, .caption, span, div')) {
        const t = textFromNode(el)
        if (t) return t
      }
    } else if (sib.nodeType === 3) {
      const t = textFromNode(sib)
      if (t) return t
    }
    sib = sib.nextSibling
  }

  return ''
}

function parseArticle(html: string) {
  try {
    const cleaned = (html || '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')

    const parser = new DOMParser()
    const doc = parser.parseFromString(cleaned, 'text/html')

    const firstImg = doc.querySelector('img')
    if (firstImg) {
      const before: ChildNode[] = []
      for (const node of Array.from(doc.body.childNodes)) {
        if (node === firstImg || (node as Element)?.querySelector?.('img')) break
        before.push(node)
      }
      const wrap = document.createElement('div')
      before.forEach((n) => wrap.appendChild(n.cloneNode(true)))
      leadingHtml.value = wrap.innerHTML.trim()
    } else {
      leadingHtml.value = doc.body.innerHTML.trim()
    }

    const imgNodes = Array.from(doc.querySelectorAll('img')).filter((img) => {
      const src = img.getAttribute('src') || ''
      return src && !src.startsWith('data:')
    })

    images.value = imgNodes.map((img) => ({
      src: img.getAttribute('src') || '',
      alt: img.getAttribute('alt') || undefined,
      caption: getCaptionFor(img),
    }))
  } catch (e) {
    leadingHtml.value = html || ''
    images.value = []
  }
}

function openLightbox(index: number) {
  activeIndex.value = index
}
function closeLightbox() {
  activeIndex.value = null
}
function showPrev() {
  if (activeIndex.value !== null && activeIndex.value > 0) activeIndex.value--
}
function showNext() {
  if (activeIndex.value !== null && activeIndex.value < images.value.length - 1) activeIndex.value++
}

function isMobile() {
  // match your “l” breakpoint; adjust if yours differs
  return window.matchMedia('(max-width: 1024px)').matches
}

function onActiveImgClick() {
  if (!isMobile()) return
  if (activeIndex.value == null) return

  // advance or loop back to first
  if (activeIndex.value < images.value.length - 1) {
    activeIndex.value++
  } else {
    activeIndex.value = 0
  }
}

const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape') closeLightbox()
}

// When the active image changes, wait for DOM, then measure
watch(activeIndex, () => {
  nextTick(() => {
    updateCaptionMaxWidth()
  })
})

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', updateCaptionMaxWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', updateCaptionMaxWidth)
})

parseArticle(props.articleHtml)
</script>

<style lang="scss">
@import '~styles/base.scss';

.intro__excerpt-cta {
  margin-top: 1em; // adjust if you want tighter/looser spacing
  display: block;

  a {
    color: inherit;
    transition: all 0.2s ease-out;
  }
  a:hover {
    color: var(--color-midgrey);
  }
}

@include breakpoint('xl+') {
  .layout-container.article-lookbook {
    --header-offset: 180px;
  }
}

@include breakpoint('l') {
  .layout-container.article-lookbook {
    --header-offset: 125px;
    padding-bottom: 280px;
  }
}

.lookbook-article .intro {
  max-width: 480px;
  margin: 0 auto 0;
  padding: 0 0;

  @include breakpoint('l') {
    padding: 0 1.5rem !important;
  }
}

.intro__meta {
  display: grid;
  align-items: baseline;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  margin-bottom: 4px;
}

.intro__title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  @include type-xs-uppercase;
}
.intro__bullet {
  font-size: 8px;
  line-height: 1;
  margin-bottom: 2px;

  @include breakpoint('l') {
    margin-bottom: 0 !important;
    font-size: 6px !important;
  }
}

.intro__date {
  white-space: nowrap;
  @include type-xxs-uppercase;
  transform: translatey(1px);
}

.intro__excerpt {
  margin: 8px 0 14px;
}

/* two-column layout for the leading text */
.intro__leading {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: 8px;

  p:not(strong) {
    @include type-xs;
  }

  p strong {
    @include type-xxs-uppercase;
    line-height: 14px !important;
  }

  :where(p, ul, ol) {
    margin: 0 0 8px;
  }
}

.intro__leading::before {
  content: '';
  grid-column: 1;
  grid-row: 2;
}

.intro__leading :where(p, ul, ol) {
  margin: 0 0 8px;
}

/* --- Grid --- */
.lookbook-article .grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  max-width: 900px;
  margin: 0 auto;
  column-gap: 135px;
  row-gap: 120px;
  padding: 200px 20px 120px;

  @include breakpoint('l') {
    grid-template-columns: repeat(3, 1fr);
    column-gap: 16px;
    row-gap: 32px;
    padding-top: 120px !important;
    padding-bottom: 0 !important;
  }
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.grid-item img {
  width: 100%;
  display: block;
  object-fit: cover;
  border: 1px solid transparent;
  transition: border-color 0.2s ease;

  @include breakpoint('l') {
    border-color: black !important;
  }
}

.grid-item img:hover {
  border-color: black;
}

.grid-item:hover .counter {
  opacity: 1 !important;
}

.counter {
  margin-top: 6px;
  @include type-xxs-uppercase;
  opacity: 0.5;
  align-self: flex-start;
  transition: opacity 0.2s ease;

  @include breakpoint('l') {
    opacity: 1 !important;
  }
}

/* --- Lightbox --- */

@include breakpoint('l') {
  .thumb-wrapper {
    display: none !important;
  }

  .lightbox-image-fade-enter-active,
  .lightbox-image-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .lightbox-image-fade-enter-from {
    opacity: 0;
  }
  .lightbox-image-fade-leave-to {
    opacity: 0;
  }
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

/* Smooth fade when switching active images */
.lightbox-image-fade-enter-active,
.lightbox-image-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-image-fade-enter-from,
.lightbox-image-fade-leave-to {
  opacity: 0;
}

.close-btn {
  display: none;
  top: 20px !important;
  @include type-xxs-uppercase;
  color: black;
  position: absolute;
  right: 20px;

  @include breakpoint('l') {
    display: block !important;
    @include type-xxs-uppercase;
  }
}

.lightbox {
  position: fixed;
  inset: 0;
  background: #fdfdfd;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 80%;
  max-height: 90%;

  @include breakpoint('xl') {
    max-width: 62% !important;
    max-height: unset !important;
    transform: translatey(-10%);
  }

  @include breakpoint('l') {
    max-width: 100% !important;
    transform: none !important;
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
  }
}

.lightbox img.active-img {
  display: block;
  max-width: 100%;
  max-height: 70svh;
  object-fit: contain;
}

.caption-row {
  display: flex;
  gap: 20px;
  @include type-xxs-uppercase;
  width: 100%;
  justify-content: flex-start;
  margin-top: 8px;
  transition: width 0.2s ease;
}

/* Fade for the caption row */
.caption-fade-enter-active,
.caption-fade-leave-active {
  transition: opacity 0.2s ease;
}

.caption-fade-enter-from,
.caption-fade-leave-to {
  opacity: 0;
}

.lightbox-counter {
  @include type-xxs-uppercase;
}

.caption {
  @include type-xxs-uppercase;
  white-space: normal;
}

/* Thumbs */
.thumb-wrapper {
  position: absolute;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.thumb-wrapper:hover {
  opacity: 1;
}

.prev-thumb {
  left: 20px;
}

.next-thumb {
  right: 20px;
}

.thumb {
  width: 100%;
  max-width: 100px;
  height: 100%;
  overflow: hidden;
  line-height: 0;
  border: 1px solid transparent;
  transition: border-color 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb:hover {
  border-color: black;
}

.thumb-counter {
  margin-top: 6px;
  @include type-xxs-uppercase;
}
</style>
